import { getWebPath, TableDetailsTabType } from '@doss/common-all'
import { orderBy } from 'lodash'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import {
  BreadcrumbObjectType,
  ChartType,
  useGetDetailedTableQuery,
} from '~/common/generated/apollo-operations'
import { TableSource } from '~/components/shared/DossTable'
import { TableErrorState } from '~/components/shared/Table/TableErrorState'
import { useGlobalContext } from '../../common/hooks/useGlobalContext'
import {
  retrieveLastViewedPivotTable,
  retrieveLastViewedTab,
  storeLastViewedPivotTable,
  storeLastViewedTab,
} from '../../common/utils/local-storage'
import {
  BasicChartInTable,
  DetailedTable,
  DetailedTableColumnWithTable,
  DetailedTableTab,
  TableType,
} from '../../components/tables/types'

interface TableDetailsContextValue {
  tableId: string
  tableType: TableType
  table: DetailedTable | null
  tableColumns: DetailedTableColumnWithTable[]
  parentTableColumns: DetailedTableColumnWithTable[]
  childTableColumns: DetailedTableColumnWithTable[]
  tableTabs: DetailedTableTab[]
  pivotTables: BasicChartInTable[]
  selectedTab: DetailedTableTab | null
  selectedPivotTable: BasicChartInTable | null
  setSelectedTabId: (tabId: string) => void
  setSelectedPivotTableId: (pivotTableId: string) => void
  hideToolbarButtons: boolean
  setHideToolbarButtons: (hide: boolean) => void
  setNavigateToTabAfterCreation: (tabId: string) => void
  loading: boolean
  isInitialLoadComplete: boolean // True when table data and selected table tab become defined after initial load
  activeTabType: TableDetailsTabType
  setActiveTabType: (tabType: TableDetailsTabType) => void
  isChangingTabType: boolean
}

export function TableDetailsProvider({
  children,
  tableId,
  parentTableId,
  tableType,
  tableSource,
  // If true, the provider will not redirect (used for chart tables)
  noRedirect = false,
}: {
  children: React.ReactNode
  tableId: string
  parentTableId?: string | null
  tableType: TableType
  tableSource: TableSource
  noRedirect?: boolean
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { user } = useGlobalContext()
  const {
    data: tableData,
    loading,
    error: tableError,
    refetch: refetchTable,
  } = useGetDetailedTableQuery({
    variables: {
      tableId,
    },
    skip: !tableId,
  })

  // For drilldown tables, we need to fetch the parent table to get the parent table filters
  // and group bys
  const {
    data: parentTableData,
    loading: parentTableLoading,
    error: parentTableError,
    refetch: refetchParentTable,
  } = useGetDetailedTableQuery({
    variables: {
      tableId: parentTableId ?? '',
    },
    skip: !parentTableId || tableSource !== TableSource.PIVOT_TABLE_DRILLDOWN,
  })

  const childTableColumns = useMemo(() => {
    return (
      tableData?.table.childTables.flatMap((childTable) =>
        childTable.columns.map((column) => ({
          ...column,
          table: { __typename: 'Table', id: childTable.id, name: childTable.name },
        })),
      ) || []
    )
  }, [tableData?.table.childTables])

  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false)
  const [selectedTab, setSelectedTab] = useState<DetailedTableTab | null>(null)
  const [selectedPivotTable, setSelectedPivotTable] = useState<BasicChartInTable | null>(null)
  const [navigateToTabAfterCreation, setNavigateToTabAfterCreation] = useState<string | null>(null)
  const [hideToolbarButtons, setHideToolbarButtons] = useState(true)
  // Tracks if changing tab type from view to pivot or pivot to view to prevent infinite loading or 404 not found
  const [isChangingTabType, setIsChangingTabType] = useState(false)

  const lastViewedTabId = useMemo(
    () => (user ? retrieveLastViewedTab(user.id, tableId) : null),
    [tableId, user],
  )

  const lastViewedPivotTableId = useMemo(
    () => (user ? retrieveLastViewedPivotTable(user.id, tableId) : null),
    [tableId, user],
  )

  // Determine which tab type we're viewing based on query params
  const activeTabType = useMemo(() => {
    // If 'tab' query param exists, use it directly
    if (searchParams.get('tab')) {
      return searchParams.get('tab') as TableDetailsTabType
    }
    // Otherwise, infer from the presence of 'view' or 'pivot' params
    if (searchParams.get('pivot')) {
      return TableDetailsTabType.PIVOT_TABLES
    }
    // By default, show the table tabs
    return TableDetailsTabType.TABLE_TABS
  }, [searchParams])

  // Get the active item ID based on tab type
  const navigationTabId = useMemo(() => {
    if (activeTabType === TableDetailsTabType.PIVOT_TABLES) {
      return searchParams.get('pivot') ?? undefined
    }
    return searchParams.get('view') ?? undefined
  }, [activeTabType, searchParams])

  const setLastViewedTabId = useCallback(
    (tabId: string) => {
      if (user?.id && tableId) {
        storeLastViewedTab(user.id, tableId, tabId)
      }
    },
    [tableId, user?.id],
  )

  const setLastViewedPivotTableId = useCallback(
    (pivotTableId: string) => {
      if (user?.id && tableId) {
        storeLastViewedPivotTable(user.id, tableId, pivotTableId)
      }
    },
    [tableId, user?.id],
  )

  const table = useMemo(() => {
    return tableData?.table || null
  }, [tableData?.table])

  const tableColumns = useMemo(() => {
    return (
      table?.columns.map((column) => ({
        ...column,
        table: { __typename: 'Table', id: table.id, name: table.name },
      })) || []
    )
  }, [table?.columns, table?.id, table?.name])

  const parentTableColumns = useMemo(() => {
    return (
      parentTableData?.table.columns.map((column) => ({
        ...column,
        table: {
          __typename: 'Table',
          id: parentTableData.table.id,
          name: parentTableData.table.name,
        },
      })) || []
    )
  }, [parentTableData?.table.columns, parentTableData?.table.id, parentTableData?.table.name])

  const tableTabs = useMemo(() => {
    return table?.tableTabs || []
  }, [table?.tableTabs])

  const pivotTables = useMemo(() => {
    return table?.charts.filter((chart) => chart.chartType === ChartType.PIVOT_TABLE) || []
  }, [table?.charts])

  const setSelectedTabId = useCallback(
    (tabId: string) => {
      if (tableType !== TableType.TABLE) {
        // If the table is coming from a chart or backlink or other, just use the first tab
        const sortedTableTabs = orderBy(tableTabs, (tab) => tab.order)
        setSelectedTab(sortedTableTabs.at(0) ?? null)
      } else if (tableTabs.some((tab) => tab.id === tabId)) {
        // Only set the selected tab if it exists in the table tabs
        setLastViewedTabId(tabId)
        setSelectedTab(tableTabs.find((tab) => tab.id === tabId) ?? null)
      }
      if (noRedirect) {
        return
      }

      // Update the URL query params
      const asPath = location.pathname + location.search + location.hash
      if (
        asPath.includes(tableId) &&
        (searchParams.get('view') !== tabId || activeTabType !== TableDetailsTabType.TABLE_TABS)
      ) {
        // Replace makes sure the back button works as expected while shallow prevents a full page reload
        navigate(
          getWebPath({
            objectId: tableId,
            objectType: BreadcrumbObjectType.TABLE,
            tableParams: { tab: TableDetailsTabType.TABLE_TABS, id: tabId },
          }),
          { replace: true },
        )

        // Hide the toolbar buttons when navigating to a different tab to prevent flickering of the save/reset buttons
        setHideToolbarButtons(true)
      }
    },
    [
      tableType,
      tableTabs,
      noRedirect,
      navigate,
      location,
      searchParams,
      tableId,
      activeTabType,
      setLastViewedTabId,
    ],
  )

  const setSelectedPivotTableId = useCallback(
    (pivotTableId: string) => {
      // Only set the selected pivot table if it exists in the pivot tables
      if (pivotTables.some((pivotTable) => pivotTable.id === pivotTableId)) {
        setLastViewedPivotTableId(pivotTableId)
        setSelectedPivotTable(
          pivotTables.find((pivotTable) => pivotTable.id === pivotTableId) ?? null,
        )
      }
      if (noRedirect) {
        return
      }

      // Update the URL query params
      const asPath = location.pathname + location.search + location.hash
      if (
        asPath.includes(tableId) &&
        (searchParams.get('pivot') !== pivotTableId ||
          activeTabType !== TableDetailsTabType.PIVOT_TABLES)
      ) {
        navigate(
          getWebPath({
            objectId: tableId,
            objectType: BreadcrumbObjectType.TABLE,
            tableParams: { tab: TableDetailsTabType.PIVOT_TABLES, id: pivotTableId },
          }),
          { replace: true },
        )

        // Hide the toolbar buttons when navigating to a different tab to prevent flickering of the save/reset buttons
        setHideToolbarButtons(true)
      }
    },
    [
      pivotTables,
      noRedirect,
      navigate,
      location,
      searchParams,
      tableId,
      activeTabType,
      setLastViewedPivotTableId,
    ],
  )

  // Sets which tab type is active (without selecting a specific item)
  const setActiveTabType = useCallback(
    (tabType: TableDetailsTabType) => {
      if (noRedirect) {
        setActiveTabType(TableDetailsTabType.TABLE_TABS)
        return
      }

      if (tabType === TableDetailsTabType.TABLE_TABS) {
        // If switching to table tabs, use the first available tab
        const firstTabId = orderBy(tableTabs, 'order').at(0)?.id
        if (firstTabId) {
          setSelectedTabId(firstTabId)
        } else {
          // No tabs available, just set the tab type
          navigate(
            getWebPath({
              objectId: tableId,
              objectType: BreadcrumbObjectType.TABLE,
              tableParams: { tab: TableDetailsTabType.TABLE_TABS },
            }),
            { replace: true },
          )
        }
      } else {
        // If switching to pivot tables, use the first available pivot table
        const firstPivotId = pivotTables.at(0)?.id
        if (firstPivotId) {
          setSelectedPivotTableId(firstPivotId)
        } else {
          // No pivot tables available, just set the tab type
          navigate(
            getWebPath({
              objectId: tableId,
              objectType: BreadcrumbObjectType.TABLE,
              tableParams: { tab: TableDetailsTabType.PIVOT_TABLES },
            }),
            { replace: true },
          )
        }
      }
    },
    [
      noRedirect,
      tableTabs,
      setSelectedTabId,
      navigate,
      tableId,
      pivotTables,
      setSelectedPivotTableId,
    ],
  )

  // This effect handles initial loading of tabs based on URL params
  useEffect(() => {
    // Skip if we haven't loaded table data or if we've already completed initial load
    if (loading || parentTableLoading || !table) {
      return
    }

    if (!isInitialLoadComplete || isChangingTabType) {
      // Initialize the appropriate selection based on active tab type
      if (activeTabType === TableDetailsTabType.TABLE_TABS) {
        // Only set tab if we don't have one selected already
        if (!selectedTab) {
          if (navigationTabId && tableTabs.some((tab) => tab.id === navigationTabId)) {
            setSelectedTabId(navigationTabId)
          } else if (lastViewedTabId && tableTabs.some((tab) => tab.id === lastViewedTabId)) {
            setSelectedTabId(lastViewedTabId)
          } else if (tableTabs.length > 0) {
            const firstTab = orderBy(tableTabs, 'order').at(0)?.id
            firstTab && setSelectedTabId(firstTab)
          }
        }
      } else {
        // Only set pivot table if we don't have one selected already
        if (!selectedPivotTable) {
          if (navigationTabId && pivotTables.some((pivot) => pivot.id === navigationTabId)) {
            setSelectedPivotTableId(navigationTabId)
          } else if (
            lastViewedPivotTableId &&
            pivotTables.some((pivot) => pivot.id === lastViewedPivotTableId)
          ) {
            setSelectedPivotTableId(lastViewedPivotTableId)
          } else if (pivotTables.length > 0) {
            const firstPivot = pivotTables[0]?.id
            firstPivot && setSelectedPivotTableId(firstPivot)
          }
        }
      }

      setIsInitialLoadComplete(true)
      setIsChangingTabType(false)
    }
  }, [
    activeTabType,
    navigationTabId,
    tableTabs,
    pivotTables,
    selectedTab,
    selectedPivotTable,
    lastViewedTabId,
    lastViewedPivotTableId,
    setSelectedTabId,
    setSelectedPivotTableId,
    loading,
    table,
    isChangingTabType,
    isInitialLoadComplete,
    parentTableLoading,
  ])

  // This effect is mainly for setting the selected tab whenever the tableTabs reference changes
  // The tableTabs ref can change either from adding or removing tabs, or navigating to a different table
  useEffect(() => {
    if (!tableTabs.length) {
      return
    }

    // Check whether or not the table needs to have a tab selected on initial load
    if (!isInitialLoadComplete) {
      if (activeTabType === TableDetailsTabType.TABLE_TABS) {
        // Make sure we set a selected tab that is valid, meaning it exists in the tableTabs array
        if (navigationTabId && tableTabs.some((tab) => tab.id === navigationTabId)) {
          // If the URL already has a tab, go straight to that tab
          setSelectedTabId(navigationTabId)
        } else if (
          // Our last viewed tab is retrieved from local storage
          lastViewedTabId &&
          tableTabs.some((tab) => tab.id === lastViewedTabId) &&
          selectedTab?.id !== lastViewedTabId
        ) {
          // And then it is set as the selected tab
          setSelectedTabId(lastViewedTabId)
        } else if (!tableTabs.some((tab) => selectedTab?.id === tab.id)) {
          // This runs if the table doesn't have a last viewed tab or if the last viewed tab was deleted
          const firstTab = orderBy(tableTabs, 'order').at(0)?.id
          firstTab && setSelectedTabId(firstTab)
        }
      } else {
        // Make sure we set a selected pivot table that is valid, meaning it exists in the
        // pivotTables array
        if (navigationTabId && pivotTables.some((pivot) => pivot.id === navigationTabId)) {
          // If the URL already has a pivot table, go straight to that pivot table
          setSelectedPivotTableId(navigationTabId)
        } else if (
          // Our last viewed pivot table is retrieved from local storage
          lastViewedPivotTableId &&
          pivotTables.some((pivotTable) => pivotTable.id === lastViewedPivotTableId) &&
          selectedPivotTable?.id !== lastViewedPivotTableId
        ) {
          // And then it is set as the selected pivot table
          setSelectedPivotTableId(lastViewedPivotTableId)
        } else if (!pivotTables.some((pivotTable) => selectedPivotTable?.id === pivotTable.id)) {
          // This runs if the table doesn't have a last viewed pivot table or if the last viewed
          // pivot table was deleted
          const firstPivotTable = pivotTables[0]?.id
          firstPivotTable && setSelectedPivotTableId(firstPivotTable)
        }
      }
      // Once we set the tab on initial load, we no longer need to run this effect
      // Because the user will be manually selecting the tabs from that point on
      setIsInitialLoadComplete(true)
    }
  }, [
    selectedTab,
    setSelectedTabId,
    tableTabs,
    lastViewedTabId,
    navigationTabId,
    isInitialLoadComplete,
    activeTabType,
    pivotTables,
    lastViewedPivotTableId,
    selectedPivotTable?.id,
    setSelectedPivotTableId,
  ])

  // Keep selectedTab in sync with tableTabs when the query result updates (e.g. after refetch from save).
  // This ensures getHasChangedFilters/sort use the latest saved tab data instead of a stale reference.
  useEffect(() => {
    if (
      isInitialLoadComplete &&
      activeTabType === TableDetailsTabType.TABLE_TABS &&
      selectedTab?.id &&
      tableTabs.some((tab) => tab.id === selectedTab.id)
    ) {
      setSelectedTabId(selectedTab.id)
    }
  }, [tableTabs, isInitialLoadComplete, activeTabType, selectedTab?.id, setSelectedTabId])

  // This effect runs on every new tableId we load into the provider context which can happen on route change
  useEffect(() => {
    // We want to reset the initial load trigger and hide the toolbar buttons to prevent flickering
    setIsChangingTabType(true)
    setIsInitialLoadComplete(false)
    setHideToolbarButtons(true)
  }, [tableId, activeTabType])

  // This effect is for navigating to the new tab after a tab is created
  useEffect(() => {
    // The newly created tab's ID is set to navigateToTabAfterCreation once creation is successful
    // But the tab doesn't exist in the tableTabs ref yet at that point until after a refetch
    // So we wait for the table tabs to be updated and then navigate to the tab
    // This could be done with an optimistic response but we want to make sure the tab is created successfully
    // Because undoing a navigation to a tab that wasn't created could be confusing
    if (
      navigateToTabAfterCreation &&
      tableTabs.some((tab) => tab.id === navigateToTabAfterCreation)
    ) {
      setSelectedTabId(navigateToTabAfterCreation)
      setNavigateToTabAfterCreation(null)
    }
  }, [navigateToTabAfterCreation, setSelectedTabId, tableTabs])

  // If a table schema query failed with no data, every consumer (DossTable, toolbar, tab nav)
  // would wait on `table` forever. Render the shared error state with retry wired to Apollo
  // refetch instead of rendering children. Guards mirror each query's skip prop.
  if (tableId && !tableData && tableError) {
    return <TableErrorState error={tableError} onRetry={() => refetchTable()} />
  }
  if (
    parentTableId &&
    tableSource === TableSource.PIVOT_TABLE_DRILLDOWN &&
    !parentTableData &&
    parentTableError
  ) {
    return <TableErrorState error={parentTableError} onRetry={() => refetchParentTable()} />
  }

  return (
    <TableDetailsContext.Provider
      value={{
        tableId,
        table,
        tableType,
        tableColumns,
        parentTableColumns,
        childTableColumns,
        tableTabs,
        pivotTables,
        selectedTab,
        setSelectedTabId,
        selectedPivotTable,
        setSelectedPivotTableId,
        activeTabType,
        setActiveTabType,
        hideToolbarButtons,
        setHideToolbarButtons,
        setNavigateToTabAfterCreation,
        loading: loading || parentTableLoading,
        isInitialLoadComplete,
        isChangingTabType,
      }}
    >
      {children}
    </TableDetailsContext.Provider>
  )
}

export const useTableDetails = () => {
  const context = useContext(TableDetailsContext)

  if (!context) {
    throw new Error('useTableDetails must be used within a TableDetailsProvider')
  }

  return context
}

const TableDetailsContext = createContext<TableDetailsContextValue | null>(null)
